import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	Alert,
	Badge,
	Box,
	Button,
	Divider,
	Group,
	Loader,
	Modal,
	Select,
	Text,
	Title,
} from '@mantine/core'
import { QRCodeCanvas } from 'qrcode.react'
import { getItemOptions } from '../services/actions/experienceActions'
import { getOrgCommerceSettings } from '../services/actions/orgActions'
import {
	addCheckInOnSiteManualPaidItem,
	addCheckInOnSiteFreeItem,
	createCheckInOnSiteSaleCheckout,
} from '../services/actions/checkInActions'

const formatMoneyFromCents = (amountCents) => {
	const amount = Number(amountCents || 0)
	if (!Number.isFinite(amount)) return '$0.00'
	return `$${(amount / 100).toFixed(2)}`
}

const parseListPriceCents = (value) => {
	const normalized = String(value ?? '')
		.trim()
		.replace(/^\$/, '')
	if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null
	const [wholePart, fractionPart = ''] = normalized.split('.')
	return Number(wholePart) * 100 + Number((fractionPart + '00').slice(0, 2))
}

const getItemOptionId = (item) => {
	const rawId =
		item?.id ??
		item?.itemOptionId ??
		item?.itemOption?.id ??
		item?.bookingItem?.itemOptionId
	if (rawId === null || rawId === undefined) return ''
	return String(rawId).trim()
}

const itemsIncludeItemOption = (items, itemOptionId) => {
	const normalizedItemOptionId = String(itemOptionId ?? '').trim()
	if (!normalizedItemOptionId || !Array.isArray(items)) return false
	return items.some((item) => getItemOptionId(item) === normalizedItemOptionId)
}

const getRefreshResultPurchasedItems = (result, checkInId) => {
	const normalizedCheckInId = String(checkInId ?? '').trim()
	if (!normalizedCheckInId || !result || typeof result !== 'object') return null

	const candidates = []
	if (result.checkInData) candidates.push(result.checkInData)
	if (result.checkIn) candidates.push(result.checkIn)
	if (Array.isArray(result.members)) candidates.push(...result.members)

	const refreshedCheckIn = candidates.find(
		(candidate) => String(candidate?.id ?? '').trim() === normalizedCheckInId,
	)
	return Array.isArray(refreshedCheckIn?.purchasedItems)
		? refreshedCheckIn.purchasedItems
		: null
}

const normalizeCheckoutItemOption = (option) => ({
	...option,
	listPriceCents: parseListPriceCents(option?.listPrice),
})

const requiresCheckoutPayment = (option) =>
	Number.isInteger(option?.listPriceCents) && option.listPriceCents > 0

const formatItemPrice = (option) =>
	requiresCheckoutPayment(option)
		? formatMoneyFromCents(option.listPriceCents)
		: 'Free'

const getTotalUnitLimit = (option) => {
	const rawLimit = option?.inventory?.totalUnitLimit ?? option?.totalUnitLimit
	if (rawLimit === null || rawLimit === undefined || rawLimit === '')
		return null
	const limit = Number(rawLimit)
	return Number.isInteger(limit) && limit >= 0 ? limit : null
}

const getAvailableUnits = (option) => {
	const limit = getTotalUnitLimit(option)
	if (limit === null) return null

	const rawAvailable = option?.inventory?.availableToAllocate
	if (rawAvailable !== null && rawAvailable !== undefined) {
		const available = Number(rawAvailable)
		if (Number.isFinite(available)) return Math.max(0, available)
	}

	const allocated = Number(option?.inventory?.allocatedUnits || 0)
	return Math.max(0, limit - (Number.isFinite(allocated) ? allocated : 0))
}

const isOptionInventoryAvailable = (option) => {
	const available = getAvailableUnits(option)
	return available === null || available > 0
}

const formatInventorySuffix = (option) => {
	const available = getAvailableUnits(option)
	return available === null ? '' : ` - ${available} left`
}

const CHECKOUT_POLL_INTERVAL_MS = 2500
const CHECKOUT_POLL_MAX_ATTEMPTS = 48
const PAYMENT_METHOD_CHECKOUT = 'Checkout'
const MANUAL_PAYMENT_METHODS = ['Venmo', 'Cash', 'Zelle', 'Other']

const isStripeCheckoutReady = (settings) =>
	Boolean(settings?.stripeConnectedAccountId) &&
	settings?.onboardingStatus === 'ENABLED' &&
	Boolean(settings?.stripeChargesEnabled) &&
	Boolean(settings?.stripePayoutsEnabled)

const PurchasedItemsModal = ({
	show,
	onClose,
	experienceSlug,
	checkInId,
	currentItems = [],
	onCheckoutCreated,
}) => {
	const { orgSlug } = useParams()
	const [itemOptions, setItemOptions] = useState([])
	const [commerceSettings, setCommerceSettings] = useState(null)
	const [selectedItemId, setSelectedItemId] = useState(null)
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
		PAYMENT_METHOD_CHECKOUT,
	)
	const [checkoutSession, setCheckoutSession] = useState(null)
	const [isLoadingOptions, setIsLoadingOptions] = useState(false)
	const [isSubmittingItem, setIsSubmittingItem] = useState(false)
	const [pollAttempts, setPollAttempts] = useState(0)
	const [error, setError] = useState('')
	const [successMessage, setSuccessMessage] = useState('')
	const [copyStatus, setCopyStatus] = useState('')

	useEffect(() => {
		if (!show) return

		setSelectedItemId(null)
		setSelectedPaymentMethod(PAYMENT_METHOD_CHECKOUT)
		setCheckoutSession(null)
		setError('')
		setSuccessMessage('')
		setCopyStatus('')
		setCommerceSettings(null)
		setIsLoadingOptions(true)

		Promise.all([
			getItemOptions(experienceSlug, orgSlug),
			getOrgCommerceSettings(orgSlug).catch((err) => {
				console.error('Failed to load commerce settings:', err)
				return null
			}),
		])
			.then(([options, commercePayload]) => {
				setItemOptions(Array.isArray(options) ? options : [])
				setCommerceSettings(commercePayload?.settings || null)
			})
			.catch((err) => {
				console.error('Failed to fetch item options:', err)
				setError('Failed to load item options.')
			})
			.finally(() => setIsLoadingOptions(false))
	}, [show, experienceSlug, orgSlug])

	const ownedItemOptionIds = useMemo(() => {
		const ids = new Set()
		if (!Array.isArray(currentItems)) return ids
		currentItems.forEach((item) => {
			const id = getItemOptionId(item)
			if (id) ids.add(id)
		})
		return ids
	}, [currentItems])

	const staffAddItems = useMemo(
		() =>
			itemOptions
				.map(normalizeCheckoutItemOption)
				.filter((option) => option.allowAddToCheckIn),
		[itemOptions],
	)
	const availableStaffAddItems = useMemo(
		() =>
			staffAddItems.filter(
				(option) =>
					!ownedItemOptionIds.has(String(option.id)) &&
					isOptionInventoryAvailable(option),
			),
		[ownedItemOptionIds, staffAddItems],
	)
	const inventoryAvailableStaffAddItems = useMemo(
		() => staffAddItems.filter(isOptionInventoryAvailable),
		[staffAddItems],
	)

	const selectedItem = staffAddItems.find(
		(option) => String(option.id) === String(selectedItemId),
	)
	const selectedItemRequiresPayment = requiresCheckoutPayment(selectedItem)
	const stripeCheckoutReady = isStripeCheckoutReady(commerceSettings)
	const paymentMethodOptions = useMemo(
		() =>
			(stripeCheckoutReady
				? [PAYMENT_METHOD_CHECKOUT, ...MANUAL_PAYMENT_METHODS]
				: MANUAL_PAYMENT_METHODS
			).map((method) => ({
				value: method,
				label: method,
			})),
		[stripeCheckoutReady],
	)
	const selectedItemIsAvailable =
		Boolean(selectedItem) && isOptionInventoryAvailable(selectedItem)
	const checkout = checkoutSession?.checkoutSession || checkoutSession
	const checkoutUrl = checkout?.checkoutUrl || ''
	const checkoutSessionId = checkout?.checkoutSessionId || ''
	const checkoutBookingItemId = checkout?.bookingItemId
		? String(checkout.bookingItemId)
		: ''
	const checkoutOrderId = checkout?.orderId ? String(checkout.orderId) : ''
	const amounts = checkout?.amounts || {}
	const totalAmountCents =
		amounts.totalAmountCents ??
		(selectedItem?.listPriceCents ? selectedItem.listPriceCents : 0)
	const completedPurchasedItem = useMemo(() => {
		if (!checkoutBookingItemId && !checkoutOrderId) return null
		return (
			(Array.isArray(currentItems) ? currentItems : []).find((item) => {
				const itemBookingItemId = item?.bookingItemId
					? String(item.bookingItemId)
					: ''
				const itemOrderId = item?.orderId ? String(item.orderId) : ''
				return (
					(checkoutBookingItemId &&
						itemBookingItemId === checkoutBookingItemId) ||
					(checkoutOrderId && itemOrderId === checkoutOrderId)
				)
			}) || null
		)
	}, [currentItems, checkoutBookingItemId, checkoutOrderId])
	const completedPaymentStatus = String(
		completedPurchasedItem?.paymentStatus || '',
	).toLowerCase()
	const isCheckoutComplete =
		Boolean(completedPurchasedItem) && completedPaymentStatus !== 'pending'

	useEffect(() => {
		if (!selectedItemRequiresPayment) {
			setSelectedPaymentMethod(PAYMENT_METHOD_CHECKOUT)
			return
		}

		setSelectedPaymentMethod(
			stripeCheckoutReady ? PAYMENT_METHOD_CHECKOUT : MANUAL_PAYMENT_METHODS[0],
		)
	}, [selectedItemId, selectedItemRequiresPayment, stripeCheckoutReady])

	useEffect(() => {
		setPollAttempts(0)
	}, [checkoutSessionId, show])

	useEffect(() => {
		if (
			!show ||
			!checkoutUrl ||
			isCheckoutComplete ||
			pollAttempts >= CHECKOUT_POLL_MAX_ATTEMPTS ||
			typeof onCheckoutCreated !== 'function'
		) {
			return undefined
		}

		const timer = window.setTimeout(async () => {
			try {
				await onCheckoutCreated({
					checkoutSession: checkout,
					reason: 'poll',
				})
			} finally {
				setPollAttempts((prev) => prev + 1)
			}
		}, CHECKOUT_POLL_INTERVAL_MS)

		return () => window.clearTimeout(timer)
	}, [
		checkout,
		checkoutUrl,
		isCheckoutComplete,
		onCheckoutCreated,
		pollAttempts,
		show,
	])

	const handleSelectedItemChange = (value) => {
		setSelectedItemId(value)
		setCheckoutSession(null)
		setError('')
		setSuccessMessage('')
		setCopyStatus('')
	}

	const handleAddSelectedItem = async () => {
		if (!checkInId || !selectedItemId || isSubmittingItem) return
		setError('')
		setSuccessMessage('')
		setCopyStatus('')
		setIsSubmittingItem(true)
		try {
			let refreshedCurrentItems = currentItems
			if (typeof onCheckoutCreated === 'function') {
				const refreshResult = await onCheckoutCreated({
					reason: 'before_item_add',
				})
				refreshedCurrentItems =
					getRefreshResultPurchasedItems(refreshResult, checkInId) ||
					refreshedCurrentItems
			}

			if (itemsIncludeItemOption(refreshedCurrentItems, selectedItemId)) {
				setSelectedItemId(null)
				setSuccessMessage('This item has already been added to the check-in.')
				return
			}

			const refreshedOptions = await getItemOptions(experienceSlug, orgSlug)
			const nextOptions = Array.isArray(refreshedOptions)
				? refreshedOptions
				: []
			setItemOptions(nextOptions)

			const refreshedSelectedItem = normalizeCheckoutItemOption(
				nextOptions.find(
					(option) => String(option.id) === String(selectedItemId),
				) || {},
			)
			if (!refreshedSelectedItem.allowAddToCheckIn) {
				setSelectedItemId(null)
				setError('This item is no longer available to add.')
				return
			}
			if (!isOptionInventoryAvailable(refreshedSelectedItem)) {
				setSelectedItemId(null)
				setError('This item is out of inventory.')
				return
			}

			if (requiresCheckoutPayment(refreshedSelectedItem)) {
				if (selectedPaymentMethod === PAYMENT_METHOD_CHECKOUT) {
					if (!stripeCheckoutReady) {
						setError('Checkout is not available for this organization.')
						return
					}

					const result = await createCheckInOnSiteSaleCheckout(
						orgSlug,
						checkInId,
						{
							itemOptionId: selectedItemId,
						},
					)
					setCheckoutSession(result)
					if (typeof onCheckoutCreated === 'function') {
						await onCheckoutCreated({
							checkoutSession: result?.checkoutSession || result,
							reason: 'created',
						})
					}
					return
				}

				const result = await addCheckInOnSiteManualPaidItem(
					orgSlug,
					checkInId,
					{
						itemOptionId: selectedItemId,
						paymentMethod: selectedPaymentMethod,
					},
				)
				setSelectedItemId(null)
				setSuccessMessage(
					`This item has been added as paid via ${selectedPaymentMethod}.`,
				)
				if (typeof onCheckoutCreated === 'function') {
					await onCheckoutCreated({
						item: result?.purchasedItem || null,
						reason: 'manual_paid_item_added',
					})
				}
				return
			}

			const result = await addCheckInOnSiteFreeItem(orgSlug, checkInId, {
				itemOptionId: selectedItemId,
			})
			setSelectedItemId(null)
			setSuccessMessage('This item has been added to the check-in.')
			if (typeof onCheckoutCreated === 'function') {
				await onCheckoutCreated({
					item: result?.purchasedItem || null,
					reason: 'free_item_added',
				})
			}
		} catch (err) {
			console.error('Failed to add item:', err)
			setError(err.message || 'Failed to add item.')
		} finally {
			setIsSubmittingItem(false)
		}
	}

	const handleCopyLink = async () => {
		if (!checkoutUrl) return
		try {
			await navigator.clipboard.writeText(checkoutUrl)
			setCopyStatus('Copied')
		} catch {
			setCopyStatus('Copy unavailable')
		}
	}

	const handleClose = () => {
		onClose()
	}

	const selectedPaymentMethodRequiresCheckout =
		selectedItemRequiresPayment &&
		selectedPaymentMethod === PAYMENT_METHOD_CHECKOUT
	const addButtonLabel = selectedPaymentMethodRequiresCheckout
		? 'Create QR'
		: 'Add Item'
	const addButtonDisabled =
		!selectedItemId ||
		!checkInId ||
		availableStaffAddItems.length === 0 ||
		!selectedItemIsAvailable ||
		(selectedItemRequiresPayment && !selectedPaymentMethod) ||
		(selectedPaymentMethodRequiresCheckout && !stripeCheckoutReady)

	return (
		<Modal
			opened={show}
			onClose={handleClose}
			title='Add Item'
			centered
			size='lg'
		>
			{error ? (
				<Alert color='red' mb='md'>
					{error}
				</Alert>
			) : null}
			{successMessage ? (
				<Alert color='teal' mb='md'>
					{successMessage}
				</Alert>
			) : null}

			{isLoadingOptions ? (
				<Group position='center' py='xl'>
					<Loader size='sm' />
				</Group>
			) : checkoutUrl ? (
				<Box>
					<Group position='apart' mb='sm'>
						<Box>
							<Title order={4} m={0}>
								{selectedItem?.name || 'Checkout'}
							</Title>
							<Text size='sm' color='dimmed'>
								{formatMoneyFromCents(totalAmountCents)}
							</Text>
						</Box>
						<Badge
							color={isCheckoutComplete ? 'teal' : 'yellow'}
							variant='light'
						>
							{isCheckoutComplete ? 'Payment complete' : 'Payment pending'}
						</Badge>
					</Group>

					{isCheckoutComplete ? (
						<Alert color='teal' mb='md'>
							This item has been added to the check-in.
						</Alert>
					) : null}

					<Group position='center' py='md'>
						<Box
							p='sm'
							style={{
								backgroundColor: '#fff',
								border: '1px solid #dee2e6',
								borderRadius: 8,
							}}
						>
							<QRCodeCanvas value={checkoutUrl} size={220} includeMargin />
						</Box>
					</Group>

					<Group position='center' spacing='sm'>
						<Button component='a' href={checkoutUrl} target='_blank'>
							Open Checkout
						</Button>
						<Button variant='outline' onClick={handleCopyLink}>
							Copy Link
						</Button>
					</Group>
					{copyStatus ? (
						<Text size='xs' color='dimmed' align='center' mt='xs'>
							{copyStatus}
						</Text>
					) : null}
				</Box>
			) : (
				<Box>
					<Select
						label='Item'
						data={availableStaffAddItems.map((option) => ({
							value: option.id,
							label: `${option.name} - ${formatItemPrice(
								option,
							)}${formatInventorySuffix(option)}`,
						}))}
						value={selectedItemId}
						onChange={handleSelectedItemChange}
						placeholder={
							availableStaffAddItems.length === 0
								? 'No items available'
								: 'Select an item'
						}
						disabled={availableStaffAddItems.length === 0}
						withinPortal
						dropdownPosition='bottom'
						maxDropdownHeight={260}
						zIndex={10000}
						required
						mb='sm'
					/>

					{selectedItem ? (
						<Box
							mt='md'
							p='sm'
							style={{
								border: '1px solid #dee2e6',
								borderRadius: 8,
							}}
						>
							<Group position='apart'>
								<Text weight={600}>{selectedItem.name}</Text>
								<Text weight={600}>{formatItemPrice(selectedItem)}</Text>
							</Group>
							{selectedItem.description ? (
								<Text size='sm' color='dimmed' mt={4}>
									{selectedItem.description}
								</Text>
							) : null}
							{getAvailableUnits(selectedItem) !== null ? (
								<Text size='sm' color='dimmed' mt={4}>
									Available to add: {getAvailableUnits(selectedItem)}
								</Text>
							) : null}
							{selectedItemRequiresPayment ? (
								<>
									<Select
										label='Payment Method'
										data={paymentMethodOptions}
										value={selectedPaymentMethod}
										onChange={(value) =>
											setSelectedPaymentMethod(
												value ||
													(stripeCheckoutReady
														? PAYMENT_METHOD_CHECKOUT
														: MANUAL_PAYMENT_METHODS[0]),
											)
										}
										withinPortal
										dropdownPosition='bottom'
										maxDropdownHeight={220}
										required
										mt='sm'
									/>
								</>
							) : null}
						</Box>
					) : null}

					{availableStaffAddItems.length === 0 ? (
						<Text size='sm' color='dimmed'>
							{staffAddItems.length === 0
								? 'No staff-addable item options are configured for this experience.'
								: inventoryAvailableStaffAddItems.length === 0
									? 'All staff-addable items are out of inventory.'
									: 'This traveler already has every available item.'}
						</Text>
					) : null}
				</Box>
			)}

			<Divider my='md' />

			<Group position='apart'>
				<span />
				<Group>
					<Button variant='subtle' onClick={handleClose}>
						{isCheckoutComplete ? 'Done' : 'Close'}
					</Button>
					{!checkoutUrl ? (
						<Button
							onClick={handleAddSelectedItem}
							loading={isSubmittingItem}
							disabled={addButtonDisabled}
						>
							{addButtonLabel}
						</Button>
					) : null}
				</Group>
			</Group>
		</Modal>
	)
}

export default PurchasedItemsModal
